import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  downloadKycDocument, useAddApplicationDocument, useApplicationDocuments,
  useApplicationTradeReferences, useRemoveApplicationDocument, useRetailerKycDocuments,
  useRetailerTradeReferences,
} from '../api/hooks';
import { ApiError } from '../api/client';
import type { KycDocument, TradeReference } from '../api/types';
import { Alert, Button, Chip, Empty, Note, Spinner, Table } from '../ui';
import { documentFormat, documentSize, uploadedByWords } from './kyc-view';

/**
 * ON-02a, ON-02, **D94** — the KYC evidence on one application, in ONE component.
 *
 * It is shown in two places and is the same thing in both: A-02, where somebody is deciding, and
 * A-04's Details tab, where somebody is looking at the account afterwards. One component rather
 * than two, because they have to agree — a reviewer who approved on three documents and an
 * account manager who later sees two would be looking at the same rows through two renderings.
 *
 * It carries BOTH halves of the evidence. Trade references were collected by the public apply
 * form from the moment they got rows of their own and shown nowhere at all: not to the person
 * who was supposed to ring them before approving, and not on the account afterwards. They sit
 * beside the documents because they answer the same question.
 *
 * Which source it reads is decided by which id it is given. On the retailer record there is no
 * application id in the page — the retailer row records which application it came from and the
 * API follows that link — so the documents shown on A-04 are by construction the pack the
 * approval was made on, not a copy that can drift from it.
 */
export function KycEvidence(
  { applicationId, retailerId, canEdit, canDownload }:
  {
    /** A-02: the application being decided. Writable while it is open. */
    applicationId?: string | undefined;
    /** A-04: the account, read through `onboardedFrom`. Read-only. */
    retailerId?: string | undefined;
    /** `retailer.approve` — may this person complete the pack? Never true on the retailer record. */
    canEdit: boolean;
    /** `retailer.approve` — may this person read the files themselves? See the route's own note. */
    canDownload: boolean;
  },
) {
  const { t } = useTranslation();

  /*
   * Both pairs of hooks are called every render and one pair of each is disabled by having no id
   * — `enabled: !!id`. Hooks cannot be called conditionally, and the alternative (two components
   * with the same body) is the duplication this component exists to avoid.
   */
  const fromApplication = useApplicationDocuments(applicationId);
  const fromRetailer = useRetailerKycDocuments(applicationId ? undefined : retailerId);
  const applicationRefs = useApplicationTradeReferences(applicationId);
  const retailerRefs = useRetailerTradeReferences(applicationId ? undefined : retailerId);

  const documents = applicationId ? fromApplication : fromRetailer;
  const references = applicationId ? applicationRefs : retailerRefs;

  return (
    <>
      <Documents
        applicationId={applicationId}
        documents={documents.data}
        loading={documents.isLoading}
        error={documents.error}
        canEdit={canEdit && !!applicationId}
        canDownload={canDownload}
      />
      <References
        references={references.data}
        loading={references.isLoading}
        promised={t('kyc.references.promised')}
      />
    </>
  );
}

/* ----------------------------------------------------------------------- documents */

function Documents(
  { applicationId, documents, loading, error, canEdit, canDownload }:
  {
    applicationId: string | undefined;
    documents: KycDocument[] | undefined;
    loading: boolean;
    error: Error | null;
    canEdit: boolean;
    canDownload: boolean;
  },
) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  // `applicationId ?? ''` is safe because the control that calls either of these only renders
  // when `canEdit` is true, which requires one.
  const add = useAddApplicationDocument(applicationId ?? '');
  const remove = useRemoveApplicationDocument(applicationId ?? '');

  const rows = documents ?? [];
  const full = rows.length >= KYC_LIMIT;

  async function choose(file: File | undefined) {
    if (!file) return;
    setFailed(null);
    try {
      await add.mutateAsync(file);
    } catch (e) {
      // The server's own sentence — it names the format it read, or the number it refused at.
      setFailed(e instanceof ApiError || e instanceof Error ? e.message : t('kyc.uploadFailed'));
    } finally {
      // So the same file can be chosen again after a refusal has been corrected.
      if (picker.current) picker.current.value = '';
    }
  }

  async function drop(doc: KycDocument) {
    setFailed(null);
    try { await remove.mutateAsync(doc.id); }
    catch (e) { setFailed(e instanceof ApiError || e instanceof Error ? e.message : t('kyc.removeFailed')); }
  }

  async function open(doc: KycDocument) {
    setFailed(null);
    try { await downloadKycDocument(doc); }
    catch (e) { setFailed(e instanceof Error ? e.message : t('kyc.downloadFailed')); }
  }

  return (
    <div className="mk-kyc">
      <div className="mk-kyc-h">
        <b>{t('kyc.title')}</b>
        <span className="mk-spacer" />
        <Chip tone={rows.length > 0 ? 'done' : 'stop'}>
          {t('kyc.ofLimit', { n: rows.length, limit: KYC_LIMIT })}
        </Chip>
      </div>

      {/*
        * The rule, said before it is hit rather than only in the refusal.
        *
        * An applicant may submit without documents and Admin cannot approve without them
        * (ON-02a) — so a reviewer meeting an empty pack needs to know, here, that approving is
        * not an option and that chasing is, and that they can file the licence themselves when
        * it arrives. The refusal from the API says the same thing; this says it first.
        */}
      {/*
        * And it is the panel's ONLY empty state. This Note and the table's own `empty` below it
        * both used to fire, so the panel said its emptiness twice in a row. The Note wins on both
        * counts: it carries the rule above rather than restating the obvious, and it is true in
        * both places this component is shown — the table's copy read "attached to this
        * application" on A-04, which is an account. The table now renders nothing when there is
        * nothing, leaving `kyc.emptyTitle`/`kyc.emptyBody` unused.
        */}
      {rows.length === 0 ? (
        <Note>{canEdit ? t('kyc.noneAdmin') : t('kyc.noneReadOnly')}</Note>
      ) : null}

      {failed ? <Alert tone="stop">{failed}</Alert> : null}
      {error ? <Alert tone="stop">{error.message}</Alert> : null}

      {loading ? <Spinner /> : null}
      {!loading && rows.length > 0 ? (
        <Table<KycDocument>
          rowKey={(d) => d.id}
          rows={rows}
          columns={[
            { header: t('kyc.file'), render: (d: KycDocument) => d.fileName },
            { header: t('kyc.format'), render: (d: KycDocument) => documentFormat(d.contentType) },
            { header: t('kyc.size'), render: (d: KycDocument) => documentSize(d.sizeBytes) },
            {
              header: t('kyc.filedBy'),
              center: true,
              /*
               * Who actually sent it. "Applicant" and "Miraki" are different kinds of evidence —
               * one is the business asserting its own identity, the other is somebody here
               * typing in what arrived by email — and the person reviewing a pack needs the
               * difference without going to the audit log.
               */
              render: (d: KycDocument) => {
                const who = uploadedByWords(d.uploadedBy, d.source);
                return <Chip tone={who === 'APPLICANT' ? 'done' : 'them'}>{t(`kyc.by.${who}`)}</Chip>;
              },
            },
            { header: t('kyc.received'), render: (d: KycDocument) => d.createdAt.slice(0, 10) },
            {
              header: '',
              render: (d: KycDocument) => (
                <span className="mk-kyc-acts">
                  {/*
                    * The DOWNLOAD is gated on the permission as well as hidden without it. The
                    * route refuses it either way — hiding a control is a display decision and
                    * never a gate — but offering a reviewer a button that answers 403 is worse
                    * than not offering it.
                    */}
                  {canDownload ? (
                    <button type="button" className="mk-btn-link" onClick={() => void open(d)}>
                      {t('kyc.download')}
                    </button>
                  ) : <span className="mk-hint">{t('kyc.downloadDenied')}</span>}
                  {canEdit ? (
                    <button
                      type="button" className="mk-btn-link"
                      disabled={remove.isPending}
                      onClick={() => void drop(d)}
                    >
                      {t('common.remove')}
                    </button>
                  ) : null}
                </span>
              ),
            },
          ]}
        />
      ) : null}

      {canEdit ? (
        <div className="mk-kyc-add">
          {/*
            * ON-02a — Admin files a document on the applicant's behalf, which is how a chase
            * finishes and the only way an application made before this release can ever be
            * approved. Deliberately NOT a link sent to the applicant: see the note at the top of
            * `kyc-documents.ts` for why a long-lived unauthenticated upload credential is the
            * one thing this design will not ship.
            */}
          <input
            ref={picker}
            id={`kyc-add-${applicationId ?? 'none'}`}
            type="file"
            accept={ACCEPT}
            disabled={full || add.isPending}
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          <Button
            size="sm" tone="ghost" disabled={full || add.isPending}
            onClick={() => picker.current?.click()}
          >
            {add.isPending ? t('kyc.adding') : t('kyc.add')}
          </Button>
          <span className="mk-hint">
            {full ? t('kyc.full', { limit: KYC_LIMIT }) : t('kyc.accepted', { limit: KYC_LIMIT, mb: MAX_MB })}
          </span>
        </div>
      ) : null}

      {/*
        * N3 is open and this says so where somebody handling the files can see it, rather than
        * only in a schema comment. It is not a disclaimer: a person about to ask an applicant for
        * a passport scan should know that nothing yet deletes it.
        */}
      <p className="mk-hint">{t('kyc.retention')}</p>
    </div>
  );
}

/* --------------------------------------------------------------- trade references */

function References(
  { references, loading, promised }:
  { references: TradeReference[] | undefined; loading: boolean; promised: string },
) {
  const { t } = useTranslation();
  const rows = references ?? [];

  return (
    <div className="mk-kyc">
      <div className="mk-kyc-h">
        <b>{t('kyc.references.title')}</b>
        <span className="mk-spacer" />
        <Chip tone={rows.length > 0 ? 'done' : 'lapse'}>{String(rows.length)}</Chip>
      </div>
      <p className="mk-hint">{promised}</p>
      {loading ? <Spinner /> : (
        <Table<TradeReference>
          rowKey={(r) => r.id}
          rows={rows}
          empty={<Empty title={t('kyc.references.emptyTitle')} body={t('kyc.references.emptyBody')} />}
          columns={[
            { header: t('kyc.references.company'), render: (r: TradeReference) => r.company },
            { header: t('kyc.references.contact'), render: (r: TradeReference) => r.contactName ?? t('common.none') },
            /*
             * The two columns that make this panel worth having. ON-02's whole purpose is that
             * somebody RINGS these before approving, and until now the portal recorded them and
             * then showed a count.
             */
            { header: t('kyc.references.email'), render: (r: TradeReference) => r.email ?? t('common.none') },
            { header: t('kyc.references.phone'), render: (r: TradeReference) => r.phone ?? t('common.none') },
            { header: t('kyc.references.relationship'), render: (r: TradeReference) => r.relationship ?? t('common.none') },
          ]}
        />
      )}
    </div>
  );
}

/*
 * The limits, restated for the screen.
 *
 * `KYC_DOCUMENT_LIMIT` and `KYC_MAX_FILE_BYTES` live in the API, which the web bundle does not
 * import — so these are a copy, and a copy has to be honest about being one. They are used for
 * what to SAY and what to disable, never for what to allow: every refusal comes from the server,
 * and the server's sentence is what gets shown. If these drift, a control is offered a moment too
 * long and the API refuses it with the real number.
 */
const KYC_LIMIT = 5;
const MAX_MB = 8;

/**
 * The file picker's filter. A convenience and not a check — a browser's `accept` is a hint that a
 * person can override in one click, and the server decides by reading the bytes.
 */
const ACCEPT = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');
